import React, { useMemo, useRef, useState, useCallback, useId } from 'react';
import Box from '@mui/material/Box';
import { scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
import { AxisBottom, AxisTop } from '@visx/axis';
import { GridColumns, GridRows } from '@visx/grid';
import {
    getReferenceCurvePaths,
    getSublevelTicks,
    getRoleGroupBands,
    getRoleGroupLabels,
    LEVEL_STEP,
} from '../utils/jacquesGeometry';
import { dashboardTokens as t } from './Dashboard/tokens';
import { withLanguage } from './common/Texts';

// Shared visual chrome for the Jacques charts (Report + Dashboard): role-group
// bands, grid, reference curves, the two-tier left axis (role group + roman
// sublevel) and the top/bottom age axes. The overlay layer (a
// candidate trace, or employee dots) is injected via the `children` render prop
// so both charts share identical styling and a single projection.
//
// Reference curves are drawn in neutral grey (chosen over the legacy green) so
// the overlay reads as the focal element.

// Major age ticks (every 5 years) carry labels and stronger grid lines; minor
// ticks (every year) draw faint lines so the reader can place an age precisely.
const AGE_MAJOR_TICKS = [20, 25, 30, 35, 40, 45, 50, 55, 60, 65];
const AGE_MINOR_TICKS = Array.from({ length: 65 - 20 + 1 }, (_, i) => 20 + i);
const SUBLEVEL_TICKS = getSublevelTicks();

// Stroke width of the green Jacques reference curves.
const REFERENCE_CURVE_WIDTH = 3;

// Width of the inner left-axis column holding the roman sublevel ticks; the rest
// of margin.left is the outer column for the role-group names.
const ROMAN_COL_W = 44;

// Level domain cropped to the labelled region: from the bottom of the lowest
// role-group band to the top of the highest one. The legacy framing
// (-1.25 .. 24) left empty gridded margins above/below the romans; we drop those
// so the plot shows only the area that carries labels. Reference curves that
// extend past the top are clipped to the plot rect.
//
// The whole domain is shifted up by half a level (LEVEL_STEP / 2) to match the
// roman labels, which sit centred between grid lines rather than on them: this
// keeps the top label off the upper edge and trims the now-empty half row below
// the lowest label.
const ROLE_BANDS = getRoleGroupBands();
const Y_DOMAIN = [
    ROLE_BANDS[0].levelBottom + LEVEL_STEP / 2,
    ROLE_BANDS[ROLE_BANDS.length - 1].levelTop + LEVEL_STEP / 2,
];
const X_DOMAIN = [20, 65];

// Splits a role-group name into at most two lines for the narrow left gutter:
// on the ' / ' separator when present, otherwise at the space nearest the middle
// for names too long to fit on one line. Short names stay on a single line.
function wrapGroupName(name) {
    if (name.includes(' / ')) return name.split(' / ');
    if (name.length <= 16) return [name];
    const mid = Math.floor(name.length / 2);
    let best = -1;
    for (let i = 0; i < name.length; i++) {
        if (name[i] === ' ' && (best < 0 || Math.abs(i - mid) < Math.abs(best - mid))) best = i;
    }
    return best > 0 ? [name.slice(0, best), name.slice(best + 1)] : [name];
}

// One uniform size for every role-group name. Kept small enough that a wrapped
// two-line label still fits the shortest (single-sublevel) band cell, e.g. the
// per-sublevel marval labels.
const ROLE_LABEL_FONT_SIZE = 11;

export default function JacquesChartBase({
    width,
    height,
    // Defaults to the localized "Age" label (resolved from `language` below);
    // pass an explicit string to override.
    ageLabel,
    margin = { top: 40, right: 36, bottom: 58, left: 156 },
    maxWidth,
    // Company chart key (Company.chart) + language select the left-axis role
    // names, mirroring the per-client legacy PNGs. Defaults render the generic
    // Spanish set.
    companyChart = '',
    language,
    // When true, drop the card chrome (border, shadow, surface fill) so the chart
    // sits flush on its surroundings — used by the report, which renders the chart
    // without a wrapping card. The dashboard keeps the framed default.
    bare = false,
    // La capa de curvas verdes de referencia. La vista de etiquetado la apaga:
    // ahí el overlay dibuja la familia completa de curvas con un mismo trazo
    // gris, y las verdes serían las mismas líneas pintadas dos veces.
    showReferenceCurves = true,
    children,
}) {
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    // Custom, instant tooltip. The native SVG <title> tooltip has a ~1s browser
    // delay; we track the hovered element here and render an absolutely
    // positioned label that appears immediately. Coordinates are taken from the
    // mouse event relative to the container, so no viewBox math is needed.
    const containerRef = useRef(null);
    const clipId = useId();
    const [tip, setTip] = useState(null);
    // `content` acepta un string o un array de strings: el mapa de talento
    // superpone varias personas en la misma coordenada y necesita listarlas
    // todas, una por línea.
    const showTip = useCallback((event, content) => {
        const lines = (Array.isArray(content) ? content : [content]).filter(Boolean);
        if (!lines.length || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        setTip({ x: event.clientX - rect.left, y: event.clientY - rect.top, lines });
    }, []);
    const hideTip = useCallback(() => setTip(null), []);

    const { xScale, yScale, project } = useMemo(() => {
        const xs = scaleLinear({ domain: X_DOMAIN, range: [0, innerW] });
        const ys = scaleLinear({ domain: Y_DOMAIN, range: [innerH, 0] });
        return { xScale: xs, yScale: ys, project: (p) => ({ x: xs(p.age), y: ys(p.level) }) };
    }, [innerW, innerH]);

    const refCurves = useMemo(() => getReferenceCurvePaths(project), [project]);
    const groups = useMemo(() => getRoleGroupBands(), []);
    // Role-band geometry is language-agnostic (keys + placement); resolve each
    // key to its localized name with the shared i18n mechanism.
    const tr = useMemo(() => withLanguage(language), [language]);
    const roleLabels = useMemo(() => getRoleGroupLabels(companyChart), [companyChart]);

    // Left-axis table: a wider outer column for the role-group names and a narrow
    // inner column (against the plot) for the roman sublevel ticks. The grid is
    // drawn in the negative-x gutter (outside the plot clip).
    const gutterLeft = -margin.left;
    const nameColRight = -ROMAN_COL_W;
    const nameColCenter = (gutterLeft + nameColRight) / 2;
    // Horizontal cell boundaries: one per sublevel (fine inner grid) and one per
    // role-group band (stronger outer grid). Levels -> y via yScale at render.
    const subBoundaries = useMemo(() => {
        const last = SUBLEVEL_TICKS[SUBLEVEL_TICKS.length - 1].level + LEVEL_STEP;
        return [...SUBLEVEL_TICKS.map((s) => s.level), last];
    }, []);
    const bandBoundaries = useMemo(() => [
        ...roleLabels.map((b) => b.levelBottom),
        Math.max(...roleLabels.map((b) => b.levelTop)),
    ], [roleLabels]);

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                width: '100%',
                maxWidth: maxWidth ?? width,
                ...(bare
                    ? {}
                    : {
                        bgcolor: t.color.surface,
                        border: `1px solid ${t.color.border}`,
                        borderRadius: `${t.radius.lg}px`,
                        boxShadow: t.shadow.sm,
                        p: 1.5,
                    }),
            }}
        >
            <svg
                viewBox={`0 0 ${width} ${height}`}
                width="100%"
                style={{ display: 'block', fontFamily: t.font.sans }}
            >
                <Group left={margin.left} top={margin.top}>
                    {/* Clip the plot so reference curves running past the top edge
                        are cut at the border instead of bleeding into the axes. */}
                    <defs>
                        <clipPath id={clipId}>
                            <rect x={0} y={0} width={innerW} height={innerH} />
                        </clipPath>
                    </defs>

                    <g clipPath={`url(#${clipId})`}>
                    {/* Role-group bands: soft tint fill + left accent bar */}
                    {groups.map((g) => {
                        const y = yScale(g.levelTop + LEVEL_STEP / 2);
                        const h = yScale(g.levelBottom + LEVEL_STEP / 2) - y;
                        const style = t.jacquesGroups[g.roman] || {};
                        return (
                            <g key={g.roman}>
                                <rect x={0} y={y} width={innerW} height={h} fill={style.tint} />
                                <rect x={0} y={y} width={3} height={h} fill={style.accent} />
                            </g>
                        );
                    })}

                    {/* Capacity-level grid: one faint line per roman sublevel */}
                    <GridRows
                        scale={yScale}
                        width={innerW}
                        tickValues={SUBLEVEL_TICKS.map((s) => s.level)}
                        stroke={t.color.divider}
                        strokeWidth={1}
                    />

                    {/* Age grid: faint minor lines every year ... */}
                    <GridColumns
                        scale={xScale}
                        height={innerH}
                        tickValues={AGE_MINOR_TICKS}
                        stroke={t.color.divider}
                        strokeWidth={0.75}
                        strokeOpacity={0.45}
                    />
                    {/* ... and stronger major lines every 5 years */}
                    <GridColumns
                        scale={xScale}
                        height={innerH}
                        tickValues={AGE_MAJOR_TICKS}
                        stroke={t.color.border}
                        strokeWidth={1.25}
                    />

                    {/* Group separators (capacity-level major divisions) */}
                    {groups.map((g) => (
                        <line
                            key={g.roman}
                            x1={0}
                            x2={innerW}
                            y1={yScale(g.levelTop + LEVEL_STEP / 2)}
                            y2={yScale(g.levelTop + LEVEL_STEP / 2)}
                            stroke="#1A1A1A"
                            strokeWidth={2.5}
                        />
                    ))}

                    {/* Reference Jacques curves — green matching the image palette */}
                    {showReferenceCurves && (
                        <>
                            <path d={refCurves.curva0} fill="none" stroke="#5FAE6E" strokeWidth={REFERENCE_CURVE_WIDTH} strokeOpacity={0.6} />
                            {refCurves.jacques.map((d, i) => (
                                <path key={i} d={d} fill="none" stroke="#3D9060" strokeWidth={REFERENCE_CURVE_WIDTH} strokeOpacity={0.7} />
                            ))}
                        </>
                    )}

                    {/* Overlay (candidate trace or employee dots). showTip/hideTip
                        read containerRef only inside event handlers, never during
                        render. */}
                    {/* eslint-disable-next-line react-hooks/refs */}
                    {children?.({ project, xScale, yScale, innerW, innerH, showTip, hideTip })}
                    </g>

                    {/* Left-axis table grid: faint inner rows (one per sublevel) in
                        the roman column, stronger outer rows (one per role-group
                        band) in the name column, plus the column dividers. */}
                    <g>
                        {subBoundaries.map((lvl, i) => (
                            <line
                                key={`sub-${i}`}
                                x1={nameColRight}
                                x2={0}
                                y1={yScale(lvl)}
                                y2={yScale(lvl)}
                                stroke={t.color.divider}
                                strokeWidth={1}
                            />
                        ))}
                        {bandBoundaries.map((lvl, i) => (
                            <line
                                key={`band-${i}`}
                                x1={gutterLeft}
                                x2={nameColRight}
                                y1={yScale(lvl)}
                                y2={yScale(lvl)}
                                stroke={t.color.border}
                                strokeWidth={1}
                            />
                        ))}
                        {[gutterLeft, nameColRight, 0].map((x) => (
                            <line key={`v-${x}`} x1={x} x2={x} y1={0} y2={innerH} stroke={t.color.border} strokeWidth={1} />
                        ))}
                        <line x1={gutterLeft} x2={0} y1={0} y2={0} stroke={t.color.border} strokeWidth={1} />
                        <line x1={gutterLeft} x2={0} y1={innerH} y2={innerH} stroke={t.color.border} strokeWidth={1} />
                    </g>

                    {/* Roman sublevel ticks, centred in their cell (half a level
                        above the grid line) and in the inner column. */}
                    {SUBLEVEL_TICKS.map((s) => (
                        <text
                            key={s.label}
                            x={nameColRight / 2}
                            y={yScale(s.level + LEVEL_STEP / 2)}
                            fontSize={11}
                            fill={t.color.fgSubtle}
                            textAnchor="middle"
                            dominantBaseline="middle"
                        >
                            {s.label}
                        </text>
                    ))}

                    {/* Role-group names, centred in their band cell (company + language) */}
                    {roleLabels.map((g, gi) => {
                        const lines = wrapGroupName(tr(g.key) || '');
                        const cy = yScale(g.levelMid);
                        return (
                            <text
                                key={`${g.key}-${gi}`}
                                x={nameColCenter}
                                y={cy}
                                fontSize={ROLE_LABEL_FONT_SIZE}
                                fontWeight={600}
                                fill={t.color.fgMuted}
                                textAnchor="middle"
                                dominantBaseline="middle"
                            >
                                {lines.map((line, i) => (
                                    <tspan
                                        key={i}
                                        x={nameColCenter}
                                        dy={i === 0 ? `${-(lines.length - 1) * 0.55}em` : '1.1em'}
                                    >
                                        {line}
                                    </tspan>
                                ))}
                            </text>
                        );
                    })}

                    {/* Age axes (top + bottom): labels on the 5-year majors */}
                    <AxisTop
                        scale={xScale}
                        top={0}
                        tickValues={AGE_MAJOR_TICKS}
                        tickFormat={(v) => `${v}`}
                        stroke={t.color.border}
                        hideTicks
                        tickLabelProps={() => ({
                            fill: t.color.fgMuted, fontSize: 14, textAnchor: 'middle', dy: '-0.4em',
                        })}
                    />
                    <AxisBottom
                        scale={xScale}
                        top={innerH}
                        tickValues={AGE_MAJOR_TICKS}
                        tickFormat={(v) => `${v}`}
                        stroke={t.color.border}
                        hideTicks
                        tickLabelProps={() => ({
                            fill: t.color.fgMuted, fontSize: 14, textAnchor: 'middle', dy: '0.4em',
                        })}
                    />
                    <text
                        x={innerW / 2}
                        y={innerH + 48}
                        fontSize={14}
                        fontWeight={600}
                        fill={t.color.fgMuted}
                        textAnchor="middle"
                    >
                        {ageLabel ?? tr('age')}
                    </text>
                </Group>
            </svg>

            {tip && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: tip.x,
                        top: tip.y,
                        transform: 'translate(12px, -50%)',
                        pointerEvents: 'none',
                        zIndex: 1,
                        maxWidth: 220,
                        px: 1,
                        py: 0.5,
                        bgcolor: t.color.primary,
                        color: '#fff',
                        fontFamily: t.font.sans,
                        fontSize: 12,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        borderRadius: `${t.radius.sm ?? 6}px`,
                        boxShadow: t.shadow.sm,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {tip.lines.map((line, i) => (
                        <Box key={i} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {line}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}
